import {useMemo} from 'react';

import {useCertificateConnectionsQuery} from './useCertificatesQuery';
import {CertificateConnection} from '../services/CertificatesService';

// FNP-1299 — WHICH CONNECTIONS MAY DELETE A CERTIFICATE.
//
// ⚠️ A SEPARATE QUESTION FROM `useCertificateRequestConnections`, and that is
// the whole ticket. Until FNP-1280 the backend derived `canRequest` and
// `canDelete` from one `writable` flag, so "may request" and "may delete" had
// one answer and the bulk-delete gate was exact BY ACCIDENT. They are now two
// declarations — `acm:RequestCertificate` and `acm:DeleteCertificate` are
// separate statements in the tenant's own role — so a tenant can hold either
// without the other and the request list is only an approximation of this one.
//
// The same two tests as the request hook, for the same reasons, both of which
// must PASS:
//   status === 'ACTIVE'          — the credential works NOW
//   canDeleteCertificates        — and the tenant declared it may DELETE
//
// ⚠️ PASSING BOTH IS STILL NOT A GUARANTEE. The declaration is made by the
// tenant and cannot be probed, so ACM may still refuse. `deleteCertificate` in
// the API refuses on `canDelete` before any AWS call and returns
// `deleted: false` with a reason, so the refusal is clean either way. This gate
// exists so the control is not OFFERED for a row that will certainly refuse.

// Exported for its own test: the rule is the point of the hook, and testing it
// through React Query would test the plumbing instead.
export const canDeleteThrough = (connection: CertificateConnection): boolean => {
    // A connection that is not ACTIVE declares nothing usable whatever it said
    // at connect time.
    if (connection.status !== 'ACTIVE') {
        return false;
    }
    // Explicitly `=== true`, not truthy: an absent value must not pass.
    return connection.canDeleteCertificates === true;
};

export const useCertificateDeleteConnections = () => {

    const {
        data,
        isLoading,
        isError,
    } = useCertificateConnectionsQuery();

    const connections = useMemo(() => data ?? [], [data]);

    // The ids, because the caller asks "may THIS row's connection delete?" per
    // row rather than iterating the list.
    const deletableConnectionIds = useMemo(
        () => new Set(connections.filter(canDeleteThrough)
                                 .map(connection => connection.id)),
        [connections],
    );

    return {
        connections: connections.filter(canDeleteThrough),
        deletableConnectionIds,
        loading: isLoading,
        isError,
    };
};
